/**
 * lazyWithRetry — wrap React.lazy() to handle Vite chunk-not-found errors
 * after a deploy.
 *
 * Background: when Netlify ships a new build, all asset hashes change.
 * A user with the OLD index.html still cached in their browser will try
 * to fetch chunks like `Pricing-C6i2qAZ0.js` that no longer exist —
 * resulting in "Failed to fetch dynamically imported module" exceptions
 * that crash the route. The fix is to detect that specific failure mode
 * once, force a hard reload (which fetches the new HTML + new hashes),
 * and let the retry land cleanly.
 *
 * Use a sessionStorage flag so we only auto-reload ONCE per chunk —
 * otherwise a genuinely-broken module would loop the page forever.
 */

import { lazy, type ComponentType } from "react";

const RELOAD_KEY = "n71:chunk-reload";
const CHUNK_ERROR_PATTERNS = [
  "Failed to fetch dynamically imported module",
  "Importing a module script failed",
  "error loading dynamically imported module",
  "Unable to preload CSS for",
];

function isChunkLoadError(err: unknown): boolean {
  const msg = (err instanceof Error ? err.message : String(err ?? "")).toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((p) => msg.includes(p.toLowerCase()));
}

/**
 * Drop-in replacement for React.lazy(() => import('...')).
 * On chunk-load failure, reloads the page once and lets the new HTML
 * resolve the import on the second attempt.
 *
 * Generic loosened to match React.lazy's actual signature
 * (T extends ComponentType<any>) — pages with route params would
 * otherwise hit type errors here that they don't hit with the
 * stock React.lazy.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
): ReturnType<typeof lazy<T>> {
  return lazy<T>(() =>
    importFn().catch((err) => {
      if (!isChunkLoadError(err)) throw err;

      // Already reloaded once for this session? Don't loop — surface
      // the error to the ErrorBoundary instead.
      const alreadyReloaded = sessionStorage.getItem(RELOAD_KEY);
      if (alreadyReloaded) {
        sessionStorage.removeItem(RELOAD_KEY);
        throw err;
      }
      sessionStorage.setItem(RELOAD_KEY, "1");
      // Hard reload — bypass cache so we definitely get fresh index.html.
      window.location.reload();
      // Hang the promise so React doesn't try to render anything before
      // the reload completes.
      return new Promise<{ default: T }>(() => {});
    }),
  );
}

/**
 * Global listener for unhandled chunk-load errors raised OUTSIDE React's
 * Suspense boundary (e.g. router preload). Belt-and-suspenders for the
 * lazyWithRetry wrapper above. Mount once at app boot.
 */
export function installChunkReloadHandler(): void {
  if (typeof window === "undefined") return;
  const handler = (event: ErrorEvent | PromiseRejectionEvent) => {
    const err = (event as PromiseRejectionEvent).reason ?? (event as ErrorEvent).error ?? (event as ErrorEvent).message;
    if (!isChunkLoadError(err)) return;
    if (sessionStorage.getItem(RELOAD_KEY)) return;
    sessionStorage.setItem(RELOAD_KEY, "1");
    window.location.reload();
  };
  window.addEventListener("error", handler);
  window.addEventListener("unhandledrejection", handler);
}
